import type { MoteStatus, MoteStatusPreview } from '@mote/api'
import {
  useMoteEditMutation,
  useMoteTranscriptTranslateMutation,
  useTrackMutation
} from '@mote/api'
import { TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import { useMoteDownload } from './useMoteDownload'

interface EditVariables extends Pick<MoteStatus, 'inMotebook'> {
  title: string
  draft?: string
  translationLanguage?: string
}

export function useMoteEdit(
  moteStatus: MoteStatus | MoteStatusPreview,
  isCreator: boolean,
  moteId: string,
  playbackLocation: string,
  onSave: (moteStatus: MoteStatus) => void,
  onTranslate?: (language: string) => void,
  isAnonymous: boolean = false
) {
  const moteEditMutation = useMoteEditMutation(moteId)
  const translateMutation = useMoteTranscriptTranslateMutation()
  const trackMutation = useTrackMutation()
  const moteDownload = useMoteDownload(moteStatus.moteId, playbackLocation)

  async function handleTranscriptTranslation(language: string) {
    const translationData = await translateMutation.mutate({
      variables: {
        moteId,
        translationLanguage: language
      }
    })

    if (translationData.error || !translationData.data) {
      return { error: true }
    }

    if (isCreator) {
      const moteEditData = await edit({
        draft: translationData.data.outputText,
        translationLanguage: language,
        inMotebook: moteStatus.inMotebook,
        title: moteStatus.displayTitle
      })

      if (moteEditData.error) {
        return { error: true }
      }
      if (onTranslate) {
        onTranslate(language)
      }
    } else {
      onSave?.({
        ...moteStatus,
        transcriptDisplay: translationData.data.outputText,
        translationLanguage: language
      } as MoteStatus)
    }

    trackMutation.mutate!({
      variables: {
        event: TRACK_EVENT_NAMES.mote.action.translation,
        languageCode: language,
        generatedBy: isCreator ? 'creator' : 'recipient'
      }
    })
    return { error: false }
  }

  async function handleEditTranscript(editedtTranscript: string) {
    return await edit(
      {
        draft: editedtTranscript,
        inMotebook: moteStatus.inMotebook,
        title: moteStatus.displayTitle
      },
      TRACK_EVENT_NAMES.mote.action.editTranscript
    )
  }

  async function handleOnSaveToMotebook(editedTitle: string) {
    return handleEditTitle(editedTitle, !moteStatus.inMotebook)
  }

  async function handleEditTitle(
    editedTitle: string,
    inMotebook = moteStatus.inMotebook
  ) {
    return await edit(
      {
        title: editedTitle,
        inMotebook
      },
      TRACK_EVENT_NAMES.mote.action.editTitle
    )
  }

  async function edit(variables: EditVariables, eventName: string = '') {
    const editedData = await moteEditMutation.mutate({
      variables: {
        ...variables,
        isAnonymous
      }
    })
    if (editedData.error || !editedData.data) {
      return { error: true }
    }
    onSave?.(editedData.data.moteStatus)
    if (eventName) {
      trackMutation.mutate!({
        variables: {
          event: eventName
        }
      })
    }
    return { error: false }
  }

  return {
    handleEditTranscript,
    handleOnDownload: moteDownload,
    handleTranscriptTranslation,
    handleOnSaveToMotebook,
    handleEditTitle
  }
}
